import type {
  Location,
  LocationTypeEnum as LocationType,
  LocationStatusEnum as LocationStatus,
  Stock,
} from '../shared/types';
import {
  LocationSchema,
  EntityType,
  EntityBase
} from '../shared/types';
import type { LocationRef } from '../shared/interfaces';
import z from 'zod';

const VERSION_ONE = 1;

export class LocationEntity {

  private constructor(private readonly props: Location) { }

  static create(props: Omit<Location, keyof z.infer<typeof EntityBase>>): LocationEntity {
    const now = new Date();
    const location = LocationSchema.parse({
      ...props,
      _id: EntityType.enum.location + globalThis.crypto.randomUUID(),
      type: EntityType.enum.location,
      version: VERSION_ONE,
      createdAt: now,
      updatedAt: now,
    });
    return new LocationEntity(location);
  }

  static restore(data: Location): LocationEntity {
    return new LocationEntity(LocationSchema.parse(data));
  }

  get version(): number {
    return this.props.version;
  }

  get id(): string {
    return this.props._id;
  }

  get _id(): string {
    return this.props._id;
  }

  get warehouseId(): string {
    return this.props.warehouseId;
  }

  get code(): string {
    return this.props.code;
  }

  get type(): LocationType {
    // @ts-ignore — Phase 2: type field stores locationType string in current schema
    return this.props.type;
  }

  // @ts-ignore — Phase 2: status not in current LocationSchema
  get status(): LocationStatus {
    // @ts-ignore
    return this.props.status;
  }

  get dimensions(): {
    length: number;
    width: number;
    height: number;
  } {
    // @ts-ignore — Phase 2: dimensions not in current LocationSchema
    return this.props.dimensions;
  }

  get capacity(): {
    maxWeight: number;
    maxPallets: number;
  } {
    // @ts-ignore — Phase 2: capacity not in current LocationSchema
    return this.props.capacity;
  }

  get restrictions(): string[] {
    // @ts-ignore — Phase 2: restrictions not in current LocationSchema
    return this.props.restrictions;
  }

  get parentId(): string | null {
    return (this.props.parentId as string | null | undefined) ?? null;
  }

  get metadata(): Record<string, any> | undefined {
    // @ts-ignore — Phase 2: metadata not in current LocationSchema
    return this.props.metadata;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }


  private _stock: Stock[] = [];
  private _children: LocationRef[] = [];

  get stock(): Stock[] {
    return [...this._stock];
  }

  get children(): LocationRef[] {
    return [...this._children];
  }

  addStock(stock: Stock): void {
    // Validate capacity
    if (!this.canAcceptStock(stock)) {
      throw new Error('Location capacity exceeded');
    }
    // Check restrictions
    if (!this.checkRestrictions(stock)) {
      throw new Error('Stock does not meet location restrictions');
    }
    this._stock.push(stock);
  }

  removeStock(stockId: string): void {
    const index = this._stock.findIndex(s => s._id === stockId);
    if (index === -1) {
      throw new Error('Stock not found in location');
    }
    this._stock.splice(index, 1);
  }

  addChild(location: LocationRef): void {
    if (location.warehouseId !== this.warehouseId) {
      throw new Error('Child location must belong to the same warehouse');
    }
    if (this._children.some(child => child.id === location.id)) {
      throw new Error('Child location already exists');
    }
    this._children.push(location);
  }

  removeChild(locationId: string): void {
    const index = this._children.findIndex(child => child.id === locationId);
    if (index === -1) {
      throw new Error('Child location not found');
    }
    // Check if child has stock
    if (this._children[index].hasStock()) {
      throw new Error('Cannot remove location with existing stock');
    }
    this._children.splice(index, 1);
  }

  hasStock(): boolean {
    return this._stock.length > 0 || this._children.some(child => child.hasStock());
  }

  getUsedArea(): number {
    // @ts-ignore — Phase 2: stock.dimensions is optional in current schema
    const stockArea = this._stock.reduce((acc, stock) => acc + (stock.dimensions?.length ?? 0) * (stock.dimensions?.width ?? 0), 0);
    const childrenArea = this._children.reduce((acc, child) => acc + child.getUsedArea(), 0);
    return stockArea + childrenArea;
  }

  getUsedPallets(): number {
    const stockPallets = this._stock.reduce((acc, stock) => acc + (stock.palletSpaces ?? 0), 0);
    const childrenPallets = this._children.reduce((acc, child) => acc + child.getUsedPallets(), 0);
    return stockPallets + childrenPallets;
  }

  getTotalWeight(): number {
    const stockWeight = this._stock.reduce((acc, stock) => acc + (stock.weight ?? 0), 0);
    const childrenWeight = this._children.reduce((acc, child) => acc + child.getTotalWeight(), 0);
    return stockWeight + childrenWeight;
  }

  private canAcceptStock(stock: Stock): boolean {
    const currentWeight = this.getTotalWeight();
    const currentPallets = this.getUsedPallets();

    return (
      currentWeight + (stock.weight ?? 0) <= this.capacity.maxWeight &&
      currentPallets + (stock.palletSpaces ?? 0) <= this.capacity.maxPallets
    );
  }

  private checkRestrictions(stock: Stock): boolean {
    // Check if stock meets all location restrictions
    return this.restrictions.every(restriction =>
      stock.metadata?.restrictions?.includes(restriction) ?? false
    );
  }

  validate(): boolean {
    try {
      LocationSchema.parse(this);
      return true;
    } catch (error) {
      return false;
    }
  }

  toJSON(): Location {
    return LocationSchema.parse(this);
  }
}

export type { Location };
